/* eslint-disable @nx/enforce-module-boundaries */
import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './_shared/guards/auth.guard';
import { ApplianceDetailsGuard } from './_shared/guards/appliance-details.guard';
import { LoaderComponent } from '@domgen/dgx-components';
import { RepairDetailsGuard } from './_shared/guards/repair-details.guard';
import { ErrorGuard } from './_shared/guards/error.guard';
import { PaymentGuard } from './_shared/guards/payment.guard';
import { OrbitClaimComponent } from './../../../../orbit-claims/src/lib/orbit-claims/orbit-claims.component';
import { EngineerBookingComponent } from './../../../../orbit-claims/src/lib/orbit-claims/engineer-booking/engineer-booking.component';
import { FaultDataComponent } from '../../../../orbit-claims/src/lib/orbit-claims/fault-date/fault-start-date.component';
import { OrbitQuestionsComponent } from '../../../../orbit-claims/src/lib/orbit-claims/orbit-questions/orbit-questions.component';
import { ValidateDetailsComponent } from './../../../../orbit-claims/src/lib/orbit-claims/validate-details/validate-details.component';
import {
  AppliancesService,
  ManageMyRepairService,
} from '@domgen/claims/feature-manage-my-repair';
import { OrbitModelNumberCaptureComponent } from 'libs/orbit-claims/src/lib/orbit-claims/components/model-number-capture/model-number-capture.component';

export const routes: Routes = [
  {
    path: '',
    component: LoaderComponent,
    pathMatch: 'full',
    canActivate: [AuthGuard],
  },
  {
    path: 'orbit/:planNumber',
    loadComponent: () => OrbitClaimComponent,
    providers: [],
    children: [
      {
        path: 'model-number',
        loadComponent: () => OrbitModelNumberCaptureComponent,
      },
      {
        path: 'fault-capture',
        loadComponent: () => OrbitModelNumberCaptureComponent,
        children: [],
      },
      {
        path: 'fault-date',
        loadComponent: () => FaultDataComponent,
      },
      {
        path: 'additional-info',
        loadComponent: () => OrbitQuestionsComponent,
      },
      {
        path: 'validate-details',
        loadComponent: () => ValidateDetailsComponent,
      },
      {
        path: 'engineer-booking',
        loadComponent: () => EngineerBookingComponent,
      },
    ],
  },
  {
    path: 'my-plans/:planNumber/claims',
    loadChildren: () =>
      import('@domgen/claims/feature-manage-my-repair').then(
        (m) => m.FeatureManageMyRepairModule
      ),
  },
  {
    path: 'gas-safety-notice',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-gas-safety').then(
        (m) => m.ClaimsFeatureGasSafetyModule
      ),
  },
  {
    path: 'smart-fix',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/smart-fix/feature-shell').then(
        (m) => m.SmartFixFeatureShellModule
      ),
  },
  {
    path: 'call-service-provider',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-call-oem').then(
        (m) => m.ClaimsFeatureCallOemModule
      ),
  },
  {
    path: 'book-collection',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-book-collection').then(
        (m) => m.ClaimsFeatureBookCollectionModule
      ),
  },
  {
    path: 'book-callback',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-book-callback').then(
        (m) => m.ClaimsFeatureBookCallbackModule
      ),
  },
  {
    path: 'book-dropoff',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./book-dropoff/book-dropoff.module').then(
        (m) => m.BookDropOffModule
      ),
  },
  {
    path: 'book-selfsend',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-book-selfsend').then(
        (m) => m.ClaimsFeatureBookSelfsendModule
      ),
  },
  {
    path: 'book-engineer',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-book-engineer').then(
        (m) => m.ClaimsFeatureBookEngineerModule
      ),
  },
  {
    path: 'appliance-details',
    canActivate: [AuthGuard, ApplianceDetailsGuard],
    loadChildren: () =>
      import('@domgen/feature-appliance-details').then(
        (m) => m.ClaimsFeatureApplianceDetailsModule
      ),
  },
  {
    path: 'confirmation',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-confirmation').then(
        (m) => m.ClaimsFeatureConfirmationModule
      ),
  },
  {
    path: 'enter-manufacturer',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-enter-manufacturer').then(
        (m) => m.ClaimsFeatureEnterManufacturerModule
      ),
  },
  {
    path: 'product-type',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@domgen/feature-product-type').then(
        (m) => m.ClaimsFeatureProductTypeModule
      ),
  },
  {
    path: 'payment',
    canActivate: [AuthGuard, PaymentGuard],
    loadChildren: () =>
      import('@domgen/feature-excess-payment').then(
        (m) => m.ClaimsFeatureExcessPaymentModule
      ),
  },
  {
    path: 'repair-details',
    canActivate: [AuthGuard, RepairDetailsGuard],
    loadChildren: () =>
      import('@domgen/feature-repair-details').then(
        (m) => m.ClaimsFeatureRepairDetailsModule
      ),
  },
  {
    path: 'error',
    canActivate: [ErrorGuard],
    loadChildren: () =>
      import('@domgen/feature-error').then((m) => m.ClaimsFeatureErrorModule),
  },
  {
    path: 'reject',
    canActivate: [ErrorGuard],
    loadChildren: () =>
      import('@domgen/feature-error').then((m) => m.ClaimsFeatureErrorModule),
  },
  {
    path: 'logout',
    canActivate: [],
    loadChildren: () =>
      import('@domgen/feature-logout').then((m) => m.ClaimsFeatureLogoutModule),
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'top', // Add options right here
      preloadingStrategy: PreloadAllModules,
    }),
  ],
  exports: [RouterModule],
  providers: [AppliancesService, ManageMyRepairService],
})
export class ClaimsFeatureShellRoutingModule {}
